import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  GraduationCap,
  Certificate,
  Clock,
  ArrowRight,
  BookOpen,
  CreditCard,
  Warning,
  CalendarCheck,
  Download,
  X,
} from "@phosphor-icons/react";
import Seo from "@/components/Seo";

const fmtWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : "—";
const fmtDay = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

const RESCHEDULE_BLOCK_HOURS = 48;
const hoursUntil = (iso) => (new Date(iso).getTime() - Date.now()) / 36e5;

export default function Dashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [enrollments, setEnrollments] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [payingId, setPayingId] = useState("");
  const [reschedTarget, setReschedTarget] = useState(null); // enrollment object

  const refresh = useCallback(async () => {
    const [{ data: enr }, { data: certs }] = await Promise.all([
      api.get("/enrollments"),
      api.get("/me/certificates").catch(() => ({ data: [] })),
    ]);
    setEnrollments(enr);
    setCertificates(certs);
  }, []);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/login?next=/dashboard");
      return;
    }
    if (user) {
      refresh()
        .catch(() => {})
        .finally(() => setFetching(false));
    }
  }, [user, loading, navigate, refresh]);

  const payBalance = async (enrollmentId) => {
    setPayingId(enrollmentId);
    try {
      const { data } = await api.post(`/checkout/balance/${enrollmentId}`, {
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      alert(e.response?.data?.detail || "Balance checkout failed. Please try again.");
      setPayingId("");
    }
  };

  const downloadCert = async (enrollmentId, certId) => {
    try {
      const res = await api.get(`/enrollments/${enrollmentId}/certificate`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SEP-Certificate-${certId || enrollmentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to download certificate.");
    }
  };

  if (loading || !user)
    return (
      <div className="container-x py-32 font-mono text-sm text-neutral-500">
        Loading...
      </div>
    );

  const completed = enrollments.filter(
    (e) => e.status === "completed" || e.progress >= 100
  ).length;

  return (
    <div data-testid="dashboard-page">
      <Seo title="Dashboard" path="/dashboard" noindex />
      <section className="border-b border-white/10 pt-24 pb-12">
        <div className="container-x">
          <div className="overline mb-4">
            // AGENT FILE — {user.email.toUpperCase()}
          </div>
          <h1 className="font-heading text-5xl md:text-6xl tracking-tighter">
            Hello, {user.name?.split(" ")[0]}.
          </h1>
          <p className="text-neutral-400 mt-2">
            Manage your enrollments, reschedule classes, and download certificates.
          </p>
          <Link to="/change-password" className="inline-flex items-center gap-2 mt-4 font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-400 hover:text-[#FFD300]" data-testid="dashboard-change-password">
            Change password →
          </Link>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-x">
          {/* Stats */}
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-16"
            data-testid="dashboard-stats"
          >
            {[
              { label: "Enrolled", value: enrollments.length, Icon: BookOpen },
              { label: "Completed", value: completed, Icon: Certificate },
              {
                label: "Scheduled",
                value: enrollments.length - completed,
                Icon: Clock,
              },
              {
                label: "Certificates",
                value: certificates.length,
                Icon: GraduationCap,
              },
            ].map((s) => (
              <div key={s.label} className="bg-[#0A0A0A] p-6">
                <s.Icon
                  size={24}
                  weight="duotone"
                  className="text-[#FFD300] mb-3"
                />
                <div className="font-heading text-3xl mb-1">{s.value}</div>
                <div className="overline">{s.label}</div>
              </div>
            ))}
          </div>

          {/* My Courses */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline mb-2">// MY COURSES</div>
              <h2 className="font-heading text-3xl tracking-tighter">
                Your enrollments
              </h2>
            </div>
            <Link to="/training" className="btn-ghost" data-testid="browse-more">
              Browse more <ArrowRight size={14} />
            </Link>
          </div>

          {fetching ? (
            <div className="font-mono text-sm text-neutral-500">Loading...</div>
          ) : enrollments.length === 0 ? (
            <div className="surface-card p-16 text-center" data-testid="no-enrollments">
              <BookOpen size={48} weight="duotone" className="text-[#FFD300] mx-auto mb-4" />
              <h3 className="font-heading text-2xl mb-3">No active enrollments</h3>
              <p className="text-neutral-400 mb-6">
                Browse the catalog to start your first course.
              </p>
              <Link to="/training" className="btn-primary inline-flex">
                Browse Catalog <ArrowRight size={14} />
              </Link>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10"
              data-testid="enrollments-grid"
            >
              {enrollments.map((e) => {
                const hasBalance =
                  e.payment_type === "deposit" &&
                  !e.balance_paid &&
                  (e.balance_due_cents || 0) > 0;
                const isCompleted = e.status === "completed" || e.progress >= 100;
                const sessStart = e.class_session?.starts_at;
                const canReschedule =
                  !isCompleted &&
                  !["no_show", "cancelled"].includes(e.status) &&
                  sessStart &&
                  hoursUntil(sessStart) >= RESCHEDULE_BLOCK_HOURS;
                return (
                  <div
                    key={e.id}
                    className="bg-[#0A0A0A] p-6 flex flex-col"
                    data-testid={`enrollment-${e.id}`}
                  >
                    <Link to={`/learn/${e.id}`} className="block lift">
                      <div className="aspect-[4/3] overflow-hidden mb-4 bg-neutral-900">
                        <img
                          src={e.course.image}
                          alt={e.course.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <span className="code-badge">{e.course.code}</span>
                        {e.course.state && (
                          <span className="state-tag">{e.course.state}</span>
                        )}
                        {isCompleted && (
                          <span
                            className="font-mono text-[9px] tracking-[0.2em] uppercase bg-emerald-500/20 text-emerald-300 px-2 py-0.5"
                            data-testid={`completed-badge-${e.id}`}
                          >
                            Completed
                          </span>
                        )}
                        {e.status === "no_show" && (
                          <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-orange-500/20 text-orange-300 px-2 py-0.5" data-testid={`no-show-badge-${e.id}`}>No-show · contact us to rebook</span>
                        )}
                        {e.status === "cancelled" && (
                          <span className="font-mono text-[9px] tracking-[0.2em] uppercase bg-red-500/20 text-red-300 px-2 py-0.5" data-testid={`cancelled-badge-${e.id}`}>Cancelled</span>
                        )}
                        {hasBalance && (
                          <span
                            className="font-mono text-[9px] tracking-[0.2em] uppercase text-black bg-[#FFD300] px-2 py-0.5"
                            data-testid={`balance-badge-${e.id}`}
                          >
                            Deposit · Balance Due
                          </span>
                        )}
                      </div>
                      <h3 className="font-heading text-lg tracking-tight mb-3 leading-tight">
                        {e.course.title}
                      </h3>
                      {e.class_session && (
                        <div className="font-mono text-[10px] text-[#FFD300] mb-3 flex items-center gap-1.5">
                          <CalendarCheck size={11} weight="duotone" />
                          {fmtDay(e.class_session.starts_at)} ·{" "}
                          {(e.class_session.venue || "").split(" — ")[0]}
                        </div>
                      )}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs font-mono mb-1.5">
                          <span className="text-neutral-500">PROGRESS</span>
                          <span className="text-[#FFD300]">{e.progress}%</span>
                        </div>
                        <div className="h-1 bg-white/10 overflow-hidden">
                          <div
                            className="h-full bg-[#FFD300] transition-all"
                            style={{ width: `${e.progress}%` }}
                          />
                        </div>
                      </div>
                      {e.certificate_id && (
                        <div className="flex items-center gap-2 mb-3 text-xs">
                          <Certificate
                            size={14}
                            weight="duotone"
                            className="text-[#F5D033]"
                          />
                          <span className="font-mono text-neutral-300">
                            {e.certificate_id}
                          </span>
                        </div>
                      )}
                    </Link>

                    {hasBalance && (
                      <div
                        className="mt-2 mb-3 border border-[#FFD300]/40 bg-[#FFD300]/5 px-3 py-3"
                        data-testid={`balance-panel-${e.id}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Warning
                            size={14}
                            weight="duotone"
                            className="text-[#FFD300]"
                          />
                          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#FFD300]">
                            Balance due before class
                          </span>
                        </div>
                        <div className="flex items-baseline justify-between mb-3">
                          <span className="font-heading text-2xl text-white">
                            ${((e.balance_due_cents || 0) / 100).toFixed(2)}
                          </span>
                          <span className="font-mono text-[10px] text-neutral-500">
                            of ${((e.total_price_cents || 0) / 100).toFixed(2)} total
                          </span>
                        </div>
                        <button
                          onClick={(ev) => {
                            ev.preventDefault();
                            ev.stopPropagation();
                            payBalance(e.id);
                          }}
                          disabled={payingId === e.id}
                          className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD300] hover:bg-[#FFE149] text-black font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2.5 transition-colors disabled:opacity-50"
                          data-testid={`pay-balance-btn-${e.id}`}
                        >
                          <CreditCard size={14} weight="duotone" />
                          {payingId === e.id ? "Redirecting…" : "Pay Balance Now"}
                        </button>
                      </div>
                    )}
                    {e.payment_type === "deposit" && e.balance_paid && (
                      <div
                        className="mt-2 mb-3 font-mono text-[10px] tracking-[0.2em] uppercase text-emerald-400"
                        data-testid={`balance-paid-${e.id}`}
                      >
                        ✓ Paid in full
                      </div>
                    )}

                    {/* Lifecycle actions */}
                    <div className="mt-2 mb-3 flex flex-col gap-2">
                      {e.has_certificate && (
                        <button
                          onClick={() => downloadCert(e.id, e.certificate_id)}
                          className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD300] hover:bg-[#FFE149] text-black font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 transition-colors"
                          data-testid={`download-cert-btn-${e.id}`}
                        >
                          <Download size={13} weight="duotone" />
                          Download Certificate
                        </button>
                      )}
                      {!isCompleted && (
                        <button
                          onClick={() => canReschedule && setReschedTarget(e)}
                          disabled={!canReschedule}
                          className="w-full inline-flex items-center justify-center gap-2 border border-white/15 hover:bg-white/5 disabled:opacity-40 disabled:hover:bg-transparent text-neutral-200 font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 transition-colors"
                          data-testid={`reschedule-btn-${e.id}`}
                          title={
                            canReschedule
                              ? "Pick a different cohort"
                              : "Reschedule window closed (under 48 h)"
                          }
                        >
                          <CalendarCheck size={13} weight="duotone" />
                          Reschedule Class
                        </button>
                      )}
                    </div>

                    <Link
                      to={`/learn/${e.id}`}
                      className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#FFD300] pt-3 mt-auto border-t border-white/10 block"
                    >
                      {isCompleted ? "Review →" : "Continue →"}
                    </Link>
                  </div>
                );
              })}
            </div>
          )}

          {/* My Certificates */}
          <div className="mt-20 mb-6">
            <div className="overline mb-2">// MY CERTIFICATES</div>
            <h2 className="font-heading text-3xl tracking-tighter">
              Certificates on file
            </h2>
          </div>
          {certificates.length === 0 ? (
            <div
              className="surface-card p-10 text-center"
              data-testid="no-certificates"
            >
              <Certificate
                size={40}
                weight="duotone"
                className="text-[#FFD300] mx-auto mb-3"
              />
              <p className="text-neutral-400">
                Your certificates will appear here after an instructor marks your
                class complete.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10"
              data-testid="certificates-grid"
            >
              {certificates.map((c) => (
                <div
                  key={c.enrollment_id}
                  className="bg-[#0A0A0A] p-5 flex flex-col gap-3"
                  data-testid={`cert-${c.enrollment_id}`}
                >
                  <div className="flex items-center gap-2">
                    <Certificate
                      size={18}
                      weight="duotone"
                      className="text-[#FFD300]"
                    />
                    <span className="code-badge">{c.course?.code}</span>
                    {c.course?.state && (
                      <span className="state-tag">{c.course.state}</span>
                    )}
                  </div>
                  <h3 className="font-heading text-lg leading-tight">
                    {c.course?.title}
                  </h3>
                  <div className="font-mono text-[11px] text-neutral-400">
                    Issued {fmtDay(c.issued_at)}
                    <br />
                    Cert No. <span className="text-[#FFD300]">{c.certificate_id}</span>
                  </div>
                  <button
                    onClick={() => downloadCert(c.enrollment_id, c.certificate_id)}
                    className="mt-auto w-full inline-flex items-center justify-center gap-2 bg-[#FFD300] hover:bg-[#FFE149] text-black font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2"
                    data-testid={`cert-download-${c.enrollment_id}`}
                  >
                    <Download size={13} weight="duotone" />
                    Download PDF
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Cancellation notice */}
          <div
            className="surface-card p-5 mt-10 text-sm text-neutral-300 flex items-start gap-3"
            data-testid="cancel-notice"
          >
            <Warning size={18} weight="duotone" className="text-[#FFD300] mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#FFD300] mb-1">
                Need to cancel a class?
              </div>
              <p className="text-neutral-400">
                Cancellations are handled by our team. Email{" "}
                <a
                  href="mailto:info@seprotection.com?subject=Cancellation%20Request"
                  className="link-gold"
                  data-testid="cancel-mailto"
                >
                  info@seprotection.com
                </a>{" "}
                with your order number and class date. Our refund policy is on the{" "}
                <Link to="/refund" className="link-gold">
                  Refund page
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {reschedTarget && (
        <RescheduleModal
          enrollment={reschedTarget}
          onClose={() => setReschedTarget(null)}
          onSuccess={async () => {
            setReschedTarget(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

function RescheduleModal({ enrollment, onClose, onSuccess }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api
      .get(`/courses/${enrollment.course.slug}/sessions`)
      .then(({ data }) => setSessions(data))
      .catch(() => setErr("Could not load available sessions."))
      .finally(() => setLoading(false));
  }, [enrollment.course.slug]);

  const currentId = enrollment.class_session?.id;
  const now = Date.now();
  const options = sessions
    .filter((s) => s.id !== currentId)
    .filter((s) => new Date(s.starts_at).getTime() - now > RESCHEDULE_BLOCK_HOURS * 36e5)
    .filter((s) => (s.capacity || 0) === 0 || (s.enrolled_count || 0) < s.capacity)
    .sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));

  const choose = async (sessionId) => {
    setSubmittingId(sessionId);
    setErr("");
    try {
      await api.post(`/enrollments/${enrollment.id}/reschedule`, {
        class_session_id: sessionId,
      });
      await onSuccess?.();
    } catch (e) {
      setErr(e.response?.data?.detail || "Reschedule failed.");
      setSubmittingId("");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="reschedule-modal"
    >
      <div
        className="bg-[#0A0A0A] border border-white/10 w-full max-w-xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div>
            <div className="overline mb-2">// RESCHEDULE CLASS</div>
            <h3 className="font-heading text-2xl tracking-tighter">
              {enrollment.course.title}
            </h3>
            <p className="text-xs text-neutral-500 font-mono mt-1">
              Current: {fmtWhen(enrollment.class_session?.starts_at)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-white"
            data-testid="reschedule-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-6">
          <p className="text-xs text-neutral-400 mb-4">
            Pick any future cohort with an open seat. Per our{" "}
            <Link to="/refund" className="link-gold">
              refund policy
            </Link>
            , classes starting within 48 hours cannot be rescheduled online — email{" "}
            <a
              href="mailto:info@seprotection.com"
              className="link-gold"
            >
              info@seprotection.com
            </a>{" "}
            instead.
          </p>
          {err && (
            <div
              className="border border-red-500/30 bg-red-500/10 text-red-200 text-xs font-mono p-3 mb-4"
              data-testid="reschedule-error"
            >
              {err}
            </div>
          )}
          {loading ? (
            <div className="font-mono text-xs text-neutral-500">
              Loading sessions…
            </div>
          ) : options.length === 0 ? (
            <div className="font-mono text-xs text-neutral-500">
              No open sessions available right now. Please email us at{" "}
              <a
                href="mailto:info@seprotection.com"
                className="link-gold"
              >
                info@seprotection.com
              </a>
              .
            </div>
          ) : (
            <div className="space-y-2" data-testid="reschedule-options">
              {options.map((s) => {
                const remaining = (s.capacity || 0) - (s.enrolled_count || 0);
                return (
                  <button
                    key={s.id}
                    onClick={() => choose(s.id)}
                    disabled={!!submittingId}
                    className="w-full text-left border border-white/10 hover:border-[#FFD300]/50 hover:bg-white/5 p-4 transition-colors disabled:opacity-50"
                    data-testid={`reschedule-option-${s.id}`}
                  >
                    <div className="font-mono text-sm text-white">
                      {fmtWhen(s.starts_at)}
                    </div>
                    <div className="text-xs text-neutral-500 font-mono mt-1">
                      {(s.venue || "").split(" — ")[0]} · {s.instructor} ·{" "}
                      <span className="text-[#FFD300]">
                        {remaining > 0 ? `${remaining} seat${remaining === 1 ? "" : "s"} left` : "waitlist"}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
